import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CustomTable from 'common/components/CustomTable'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import useFetchPedidos from '../../hooks/useFetchPedidos'

const ModalSeleccionPedidos = ({
  open,
  onClose,
  pedidosSeleccionados,
  onGuardar,
  planificacionId,
  plantaId,
}) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

  // ====== FILTROS ======
  // Filtros del formulario
  const [filtrosFormulario, setFiltrosFormulario] = useState({
    numero_pedido: '',
    cliente: '',
    articulo: '',
    palabra: '',
  })

  const [filtrosActivos, setFiltrosActivos] = useState({
    numero_pedido: '',
    cliente: '',
    articulo: '',
    palabra: '',
    planificacion_seleccionada: '',
  })

  // ===== HOOK DE FETCH =====
  // hook solamente se utiliza para búsquedas
  // Si hay planificacionId el backend ya filtra por planta via planificacion_seleccionada.
  // Si no (modo crear), pasamos plantaId directamente.
  const { response, loading, error } = useFetchPedidos({
    ...filtrosActivos,
    planta: planificacionId ? '' : (plantaId ?? ''),
  })

  // Estado local para pedidos que se muestran en la tabla
  const [pedidosLocal, setPedidosLocal] = useState([])

  // ===== SELECCIÓN =====
  const [seleccionados, setSeleccionados] = useState([])
  const [pedidosCompletosSeleccionados, setPedidosCompletosSeleccionados] = useState([])

  // cuando llegan resultdos nuevos, actualizar el estado local
  useEffect(() => {
    setPedidosLocal(response)
  }, [response])
  useEffect(() => {
    if (error) {
      enqueueSnackbar('Error al buscar pedidos: ' + error.message, { variant: 'error' })
    }
  }, [error])

  // Limpiar estado al cerrar el modal

  useEffect(() => {
    if (!open) {
      // Reset filtros
      setFiltrosFormulario({
        numero_pedido: '',
        cliente: '',
        articulo: '',
        palabra: '',
      })
      setFiltrosActivos({
        numero_pedido: '',
        cliente: '',
        articulo: '',
        palabra: '',
        planificacion_seleccionada: '',
      })
      // Limpiar pedidos mostrados
      setPedidosLocal([])
      // Limpiar pedidos completos seleccionados
      setPedidosCompletosSeleccionados([])
    }
  }, [open])

  // Solo inicializa pedidos seleccionados al abrir
  useEffect(() => {
    if (open) {
      const idsSeleccionados = pedidosSeleccionados?.map((p) => p.id) || []
      setSeleccionados(idsSeleccionados)
      // Inicializar también los objetos completos
      setPedidosCompletosSeleccionados(pedidosSeleccionados || [])
    }
  }, [open, pedidosSeleccionados])

  // Marcar automáticamente pedidos que ya están seleccionados en la planificación
  useEffect(() => {
    if (response.length > 0 && planificacionId) {
      // Extraer IDs de pedidos que tienen id_planificacion_pedido
      const idsConSeleccion = response
        .filter((pedido) => pedido.id_planificacion_pedido)
        .map((pedido) => pedido.id)

      // Extraer objetos completos de pedidos marcados
      const pedidosMarcados = response.filter((pedido) => pedido.id_planificacion_pedido)

      // Solo actualizar si hay nuevos IDs para agregar
      if (idsConSeleccion.length > 0) {
        setSeleccionados((prevSeleccionados) => {
          // Combinar con selecciones actuales usando la versión más reciente del estado
          const idsUnicos = [...new Set([...prevSeleccionados, ...idsConSeleccion])]
          return idsUnicos
        })

        // Actualizar también los objetos completos
        setPedidosCompletosSeleccionados((prevCompletos) => {
          // Crear array temporal con los que ya teníamos
          const todosLosPedidos = [...prevCompletos]

          // Añadir los nuevos pedidos marcados que no estén ya en el array
          for (const pedido of pedidosMarcados) {
            if (!todosLosPedidos.some((p) => p.id === pedido.id)) {
              todosLosPedidos.push(pedido)
            }
          }

          return todosLosPedidos
        })
      }
    }
  }, [response, planificacionId])

  // ==== COLUMNAS =====
  const columns = [
    {
      key: 'Estado',
      value: 'tiene_incidencia',
      width: 70,
      renderCell: (row) => {
        if (!row.tiene_incidencia) return null
        return (
          <Tooltip title={row.observaciones_incidencia || 'Pedido con incidencia'} arrow>
            <WarningAmberIcon color="error" fontSize="small" />
          </Tooltip>
        )
      },
    },
    {
      key: 'Nº Pedido',
      value: 'numero_pedido',
      width: 120,
    },
    {
      key: 'Cliente',
      value: 'cliente',
      width: 150,
    },
    {
      key: 'Artículo',
      value: 'articulo',
      flex: 1,
    },
    {
      key: 'Fecha Entrega',
      value: 'fecha_entrega',
      width: 150,
      renderCell: (row) => {
        if (!row.fecha_entrega) return ''
        return new Date(row.fecha_entrega).toLocaleDateString('es-ES')
      },
    },
    {
      key: 'Cantidad',
      value: 'cantidad',
      width: 120,
      renderCell: (row) => {
        if (!row.cantidad) return ''
        return parseFloat(row.cantidad).toFixed(2)
      },
    },
    {
      key: 'Familia',
      value: 'familia',
      width: 120,
    },
  ]

  // ===== HANDLERS =====
  const handleGuardar = () => {
    onGuardar(pedidosCompletosSeleccionados)
    onClose()
  }

  const handleBuscar = () => {
    const hayFiltros =
      filtrosFormulario.numero_pedido ||
      filtrosFormulario.cliente ||
      filtrosFormulario.articulo ||
      filtrosFormulario.palabra

    if (!hayFiltros) {
      enqueueSnackbar('Por favor, introduzca al menos un criterio de búsqueda', {
        variant: 'warning',
      })
      return
    }

    // Validar longitud mínima
    const longitudMinima = 2
    const algunFiltroValido =
      (filtrosFormulario.numero_pedido &&
        filtrosFormulario.numero_pedido.length >= longitudMinima) ||
      (filtrosFormulario.cliente && filtrosFormulario.cliente.length >= longitudMinima) ||
      (filtrosFormulario.articulo && filtrosFormulario.articulo.length >= longitudMinima) ||
      (filtrosFormulario.palabra && filtrosFormulario.palabra.length >= longitudMinima)

    if (!algunFiltroValido) {
      enqueueSnackbar('Por favor, introduzca al menos 2 caracteres en algún filtro', {
        variant: 'warning',
      })
      return
    }

    // Activar los filtros
    setFiltrosActivos({
      numero_pedido: filtrosFormulario.numero_pedido,
      cliente: filtrosFormulario.cliente,
      articulo: filtrosFormulario.articulo,
      palabra: filtrosFormulario.palabra,
      planificacion_seleccionada: planificacionId || '',
    })
  }

  // Pulsar Enter = click en Buscar
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleBuscar()
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="lg" fullWidth>
      <DialogTitle>Seleccionar Pedidos</DialogTitle>

      <DialogContent>
        {/* Filtros */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', mt: 1 }}>
          <TextField
            label="Nº Pedido"
            value={filtrosFormulario.numero_pedido}
            onChange={(e) => {
              const newValue = e.target.value
              setFiltrosFormulario({ ...filtrosFormulario, numero_pedido: newValue })
              // Si el usuario borra todo, limpiar también filtrosActivos
              if (newValue === '') {
                setFiltrosActivos({ ...filtrosActivos, numero_pedido: '' })
              }
            }}
            onKeyDown={handleKeyDown}
            size="small"
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="Cliente"
            value={filtrosFormulario.cliente}
            onChange={(e) => {
              const newValue = e.target.value
              setFiltrosFormulario({ ...filtrosFormulario, cliente: newValue })
              if (newValue === '') {
                setFiltrosActivos({ ...filtrosActivos, cliente: '' })
              }
            }}
            onKeyDown={handleKeyDown}
            size="small"
            sx={{ minWidth: 200, flex: 1 }}
          />
          <TextField
            label="Artículo"
            value={filtrosFormulario.articulo}
            onChange={(e) => {
              const newValue = e.target.value
              setFiltrosFormulario({ ...filtrosFormulario, articulo: newValue })
              if (newValue === '') {
                setFiltrosActivos({ ...filtrosActivos, articulo: '' })
              }
            }}
            onKeyDown={handleKeyDown}
            size="small"
            sx={{ minWidth: 200, flex: 1 }}
          />
          <TextField
            label="Palabra Clave"
            value={filtrosFormulario.palabra}
            onChange={(e) => {
              const newValue = e.target.value
              setFiltrosFormulario({ ...filtrosFormulario, palabra: newValue })
              if (newValue === '') {
                setFiltrosActivos({ ...filtrosActivos, palabra: '' })
              }
            }}
            onKeyDown={handleKeyDown}
            size="small"
            sx={{ minWidth: 200, flex: 1 }}
          />
          <Button variant="contained" onClick={handleBuscar} sx={{ minWidth: 120, height: 40 }}>
            Buscar
          </Button>
        </Box>

        {/* Mensaje si no hay resultados */}
        {!loading && response.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {filtrosActivos.numero_pedido ||
              filtrosActivos.cliente ||
              filtrosActivos.articulo ||
              filtrosActivos.palabra
                ? 'No se encontraron pedidos con los filtros aplicados'
                : 'Usa los filtros para buscar pedidos'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Introduce al menos un criterio de búsqueda
            </Typography>
          </Box>
        )}
        {/* Tabla con checkboxes */}
        {pedidosLocal.length > 0 && (
          <CustomTable
            dataSource={pedidosLocal}
            fields={columns}
            rowid="id"
            checkbox
            selectOnClick={false}
            loading={loading}
            initialPageSize={10}
            rowSelectionModel={seleccionados}
            getRowClassName={(params) => (params.row.tiene_incidencia ? 'fila-con-incidencia' : '')}
            sx={{
              '& .fila-con-incidencia': {
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.15)',
                },
              },
            }}
            onSelectionChange={(newSelection) => {
              // Detectar si se marcó o desmarcó
              const pedidosMarcados = newSelection.filter((id) => !seleccionados.includes(id))

              const idsVisibles = pedidosLocal.map((p) => p.id)
              const pedidosDesmarcados = seleccionados.filter(
                (id) => !newSelection.includes(id) && idsVisibles.includes(id),
              )

              // Actualizar selecciones locales (IDs)
              setSeleccionados(newSelection)

              // Actualizar pedidos completos seleccionados
              setPedidosCompletosSeleccionados((prevCompletos) => {
                // Crear un array temporal
                let todosLosPedidos = [...prevCompletos]

                // Añadir los nuevos pedidos marcados (en pantalla)
                for (const pedidoId of pedidosMarcados) {
                  const pedidoCompleto = pedidosLocal.find((p) => p.id === pedidoId)
                  if (pedidoCompleto && !todosLosPedidos.some((p) => p.id === pedidoId)) {
                    todosLosPedidos.push(pedidoCompleto)
                  }
                }

                // Quitar los pedidos desmarcados
                todosLosPedidos = todosLosPedidos.filter((p) => !pedidosDesmarcados.includes(p.id))

                return todosLosPedidos
              })
            }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cerrar
        </Button>
        <Button onClick={handleGuardar} variant="contained">
          Guardar Selección ({pedidosCompletosSeleccionados.length})
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ModalSeleccionPedidos
